import { FC, useMemo, useState, useEffect } from "react";
import {
  Box,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import NextLink from "next/link";
import { BsDot } from "react-icons/bs";
import { FiStar } from "react-icons/fi";

import {
  ProductDetailsMedia,
  ProductDetailsPrice,
  Review,
} from "../../organisms";
import { CollapseCard } from "../../atoms";
import { SelectSizes } from "./SelectSizes";
import { DeliveryInformation } from "./DeliveryInformation";

import { camelCaseToTitleCase } from "../../../utils";
import PortableText from "react-portable-text";

import {
  TransformedProductDetailsEntity,
  TransformedProductEntity,
  TransformedVariantEntity,
} from "../../../types/transformed_product_entity";
import { PolicyContentBlock } from "../../molecules";
import SaleTimer from "./SaleTimer/SaleTimer";

interface Props {
  product: TransformedProductDetailsEntity;
  eventId: string;
  boughtTogetherProducts: TransformedProductEntity[];
  faq: any;
  showSaleTimer: boolean;
  pdpPageStaticContent: any;
}

export const ProductDetails: FC<Props> = ({
  product,
  eventId,
  // boughtTogetherProducts,
  faq,
  showSaleTimer,
  pdpPageStaticContent,
}) => {
  console.log(pdpPageStaticContent, "pdpPageStaticContent");
  const [selectedVariant, setSelectedVariant] =
    useState<TransformedVariantEntity | null>(null);
  const showSizeChart = useMemo(() => {
    if (selectedVariant?.shippingProfile) {
      const isValidCategory =
        product.category === "Bangle" || product.category === "Ring";
      return (
        [2, 42, 44, 45, 47, 48].includes(selectedVariant.shippingProfile.id) &&
        isValidCategory
      );
    }
    return false;
  }, [selectedVariant]);

  useEffect(() => {
    const availabelVariants = product.variants.find(
      variant => variant.availability !== "OUT-OF-STOCK"
    );
    setSelectedVariant(
      availabelVariants ? availabelVariants : product.variants[0]
    );
  }, [product]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-10">
      <div className="col-span-1 lg:col-span-6 xl:col-span-7">
        <ProductDetailsMedia medias={product.images} title={product.title} />
      </div>
      <div className="col-span-1 lg:col-span-6 xl:col-span-5 flex flex-col gap-12">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-normal">{product.title}</h1>
          <div className="flex items-center gap-2">
            <Chip
              size="small"
              label={product.sku}
              sx={{ bgcolor: "grey.50" }}
            />
            <Chip
              size="small"
              label={product.category}
              sx={{ bgcolor: "grey.50" }}
            />
            {faq && (
              <NextLink href="#faq">
                <Chip size="small" label="FAQ" sx={{ bgcolor: "grey.50" }} />
              </NextLink>
            )}
            <NextLink href="#review">
              <Chip
                size="small"
                label={product.averageRating}
                sx={{ bgcolor: "grey.50" }}
                icon={
                  <FiStar className="leading-none fill-yellow-300 stroke-yellow-600" />
                }
              />
            </NextLink>
          </div>
        </div>
        {showSaleTimer && (
          <CollapseCard title="EXPIRING SOON">
            <SaleTimer content={pdpPageStaticContent.saleTimerContent} />
          </CollapseCard>
        )}
        {selectedVariant && (
          <SelectSizes
            variants={product.variants}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            showSizeChart={showSizeChart}
            category={product.category}
          />
        )}
        {selectedVariant && (
          <ProductDetailsPrice product={product} variant={selectedVariant} />
        )}
        <CollapseCard title="best offers" defaultExpanded>
          <List dense disablePadding sx={{ listStyleType: "disc" }}>
            <Box
              component={PortableText}
              content={pdpPageStaticContent.bestOffersContent}
              serializers={{
                li: ({ children }: any) => (
                  <ListItem disableGutters>
                    <ListItemText primary={children} />
                  </ListItem>
                ),
              }}
              sx={{
                fontSize: "14px",
              }}
            />
          </List>
        </CollapseCard>

        {selectedVariant?.shippingProfile && (
          <CollapseCard title="delivery and return policy" defaultExpanded>
            <DeliveryInformation
              shippingProfile={selectedVariant.shippingProfile}
            />
          </CollapseCard>
        )}
        {product.productDetails.some(el => el) && (
          <CollapseCard title="specification" defaultExpanded>
            <Grid container spacing={2}>
              {product.productDetails.map(ele => {
                if (ele?.value) {
                  return (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          {camelCaseToTitleCase(ele.key)}
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sx={{ fontSize: "14px" }}>
                        <Typography variant="body2" fontWeight={600}>
                          {ele.value}
                        </Typography>
                      </Grid>
                    </>
                  );
                }
                return null;
              })}
            </Grid>
          </CollapseCard>
        )}
        {product.dimensions &&
          Object.values(product.dimensions).some(el => el) && (
            <CollapseCard title="dimensions" defaultExpanded>
              <Grid container spacing={2}>
                {(
                  Object.keys(product.dimensions) as Array<
                    keyof typeof product.dimensions
                  >
                ).map(key => {
                  if (!(product.dimensions && product.dimensions[key]))
                    return null;
                  return (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body2">
                          {camelCaseToTitleCase(key as string)}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight={600}>
                          {product.dimensions && product.dimensions[key]}
                        </Typography>
                      </Grid>
                    </>
                  );
                })}
              </Grid>
            </CollapseCard>
          )}
        {/* <Stack spacing={3}>
          <SectionTitle variant="subtitle2">
            Frequently Bought Together
          </SectionTitle>
          <BoughtTogetherWidget
            product={product}
            boughtTogetherProducts={boughtTogetherProducts}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
          />
        </Stack> */}
        <CollapseCard title="care tip" defaultExpanded>
          <Typography variant="body2">{product.careTip}</Typography>
        </CollapseCard>

        <CollapseCard title="disclaimer" defaultExpanded>
          <Typography variant="body2">{product.disclaimer}</Typography>
        </CollapseCard>

        {faq && (
          <div id="faq">
            <CollapseCard title={faq.title.toUpperCase()} defaultExpanded>
              {faq.content.map((item: any) => (
                <PolicyContentBlock key={item._id} item={item} />
              ))}
            </CollapseCard>
          </div>
        )}

        <Review product={product} eventId={eventId} />

        <CollapseCard title="sold by" defaultExpanded>
          <Typography variant="body2">NINJAAS LABS PVT LTD</Typography>
        </CollapseCard>
      </div>
    </div>
  );
};
